import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import axios from 'axios'
import { BASE_URL } from '../../../components/constants'
import { getTaskById, updateTask } from '../../../Api/taskApi'
import { fetchAIProfileByTask, createAIProfile, updateAIProfile } from '../../../Api/aiProfileApi'
import { useThemeColor } from '../../../hooks/useThemeColor'
import { useTranslation } from '../../../i18n/LanguageContext'
import toast from 'react-hot-toast'
import { authHeaders } from '../../../utils/authHeaders';

const UPLOAD_SOURCE_OPTIONS = [
  { value: 'computer', labelKey: 'projectSetup.uploadSourceComputer' },
  { value: 'sharepoint', labelKey: 'projectSetup.uploadSourceSharePoint' },
  { value: 'both', labelKey: 'projectSetup.uploadSourceBoth' },
]

const Toggle = ({ enabled, onToggle, aispaceColor, disabled }) => (
  <button
    type="button"
    onClick={disabled ? undefined : onToggle}
    disabled={disabled}
    className="relative inline-flex h-6 w-11 flex-shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none disabled:cursor-not-allowed disabled:opacity-60"
    style={{ backgroundColor: enabled ? aispaceColor : '#D1D5DB' }}
  >
    <span
      className="inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out"
      style={{ transform: enabled ? 'translateX(20px)' : 'translateX(0)' }}
    />
  </button>
)

const loadFromFields = (fields) => {
  const hasName = fields.some((f) => f.field_type === 'text' || f.field_type === 'textarea')
  const hasImage = fields.some((f) => f.field_type === 'image')
  const fileField = fields.find((f) => f.field_type === 'file')
  let src = 'computer'
  if (fileField?.options) {
    try { src = JSON.parse(fileField.options).uploadSource || 'computer' } catch { /* ignore */ }
  }
  return { nameEnabled: hasName, imageEnabled: hasImage, uploadEnabled: !!fileField, uploadSource: src }
}

const ProjectSetupBuilder = ({ taskId }) => {
  const { t } = useTranslation()
  const { aispaceColor } = useThemeColor()
  const { data } = useSelector((state) => state.admin)
  const isSuperAdmin = data?.user_role === 'SUPER_ADMIN'

  const [nameEnabled, setNameEnabled] = useState(false)
  const [imageEnabled, setImageEnabled] = useState(false)
  const [uploadEnabled, setUploadEnabled] = useState(false)
  const [uploadSource, setUploadSource] = useState('computer')
  const [originalState, setOriginalState] = useState(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)

  const [directChatMode, setDirectChatMode] = useState(false)
  const [directChatModeSaving, setDirectChatModeSaving] = useState(false)

  useEffect(() => {
    if (!taskId) return
    setLoading(true)
    Promise.all([
      axios.get(`${BASE_URL}/agent-custom-fields/task/${taskId}`, authHeaders()),
      getTaskById(taskId).catch(() => null),
    ])
      .then(([fieldsRes, task]) => {
        const state = loadFromFields(fieldsRes.data?.fields || [])
        setNameEnabled(state.nameEnabled)
        setImageEnabled(state.imageEnabled)
        setUploadEnabled(state.uploadEnabled)
        setUploadSource(state.uploadSource)
        setOriginalState(state)
        setDirectChatMode(!!task?.direct_chat_mode)
      })
      .catch(() => {
        const fallback = { nameEnabled: false, imageEnabled: false, uploadEnabled: false, uploadSource: 'computer' }
        setOriginalState(fallback)
      })
      .finally(() => setLoading(false))
  }, [taskId])

  const handleDirectChatModeToggle = async () => {
    if (!isSuperAdmin || !taskId || directChatModeSaving) return
    const next = !directChatMode
    setDirectChatModeSaving(true)
    try {
      await updateTask({ id: taskId, direct_chat_mode: next })

      // Direct Chat Mode implies Web Search — the chat page already hides the
      // right sidebar (Questions/Files) entirely whenever web_search_enabled
      // is true, so enabling it here also takes care of that requirement.
      if (next) {
        const existingProfile = await fetchAIProfileByTask(taskId)
        if (existingProfile?.id) {
          await updateAIProfile({ id: existingProfile.id, web_search_enabled: true })
        } else {
          const task = await getTaskById(taskId).catch(() => null)
          await createAIProfile({ task_id: taskId, name: task?.name || 'AI Profile', web_search_enabled: true })
        }
      }

      setDirectChatMode(next)
      toast.success(t('projectSetup.savedSuccess'))
    } catch (e) {
      console.error(e)
      toast.error(t('projectSetup.saveError'))
    } finally {
      setDirectChatModeSaving(false)
    }
  }

  const currentState = { nameEnabled, imageEnabled, uploadEnabled, uploadSource }
  const isDirty = originalState !== null && JSON.stringify(currentState) !== JSON.stringify(originalState)

  const handleSave = async () => {
    if (!taskId) return
    setSaving(true)
    try {
      const fields = []
      if (nameEnabled) {
        fields.push({ field_type: 'text', field_label: 'KI-Anfrage-Name', required: true, helper_text: null, default_value: null, options: null, order_index: fields.length })
      }
      if (imageEnabled) {
        fields.push({ field_type: 'image', field_label: 'Projektbild', required: false, helper_text: null, default_value: null, options: null, order_index: fields.length })
      }
      if (uploadEnabled) {
        fields.push({ field_type: 'file', field_label: 'Dokumente', required: false, helper_text: null, default_value: null, options: JSON.stringify({ uploadSource }), order_index: fields.length })
      }
      await axios.put(`${BASE_URL}/agent-custom-fields/task/${taskId}`, { fields }, authHeaders())
      const res = await axios.get(`${BASE_URL}/agent-custom-fields/task/${taskId}`, authHeaders())
      const state = loadFromFields(res.data?.fields || [])
      setOriginalState(state)
      toast.success(t('projectSetup.savedSuccess'))
    } catch (e) {
      console.error(e)
      toast.error(t('projectSetup.saveError'))
    } finally {
      setSaving(false)
    }
  }

  const handleCancel = () => {
    if (originalState) {
      setNameEnabled(originalState.nameEnabled)
      setImageEnabled(originalState.imageEnabled)
      setUploadEnabled(originalState.uploadEnabled)
      setUploadSource(originalState.uploadSource)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: aispaceColor, borderTopColor: 'transparent' }} />
      </div>
    )
  }

  return (
    <div className="px-1">
      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <h2 className="text-[20px] font-bold text-[#111827]">{t('projectSetup.title')}</h2>
          <p className="text-[13px] text-[#6B7280] mt-1">{t('projectSetup.subtitle')}</p>
        </div>
        {isSuperAdmin && (
          <div className="flex items-center gap-2">
            <button
              onClick={handleCancel}
              disabled={!isDirty || saving}
              className="px-4 py-2 text-[14px] font-medium text-[#374151] bg-white border border-[#E5E7EB] rounded-lg hover:bg-[#F9FAFB] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t('common.cancel')}
            </button>
            <button
              onClick={handleSave}
              disabled={!isDirty || saving}
              className="px-4 py-2 text-[14px] font-semibold text-white rounded-lg hover:opacity-90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ backgroundColor: aispaceColor }}
            >
              {saving ? t('common.loading') : t('projectSetup.saveChanges')}
            </button>
          </div>
        )}
      </div>

      {/* Direct Chat Mode */}
      <div className="bg-white border border-[#E5E7EB] rounded-xl px-5 py-4 mb-4">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2">
              <p className="text-[14px] font-semibold text-[#111827]">{t('projectSetup.directChatModeLabel')}</p>
              {directChatMode && (
                <span
                  className="text-[11px] font-medium px-2 py-0.5 rounded-full"
                  style={{ backgroundColor: `${aispaceColor}15`, color: aispaceColor }}
                >
                  {t('projectSetup.directChatModeEnabledBadge')}
                </span>
              )}
            </div>
            <p className="text-[12px] text-[#6B7280] mt-0.5">{t('projectSetup.directChatModeDesc')}</p>
          </div>
          <Toggle
            enabled={directChatMode}
            onToggle={handleDirectChatModeToggle}
            aispaceColor={aispaceColor}
            disabled={!isSuperAdmin || directChatModeSaving}
          />
        </div>
      </div>

      {/* Fixed field rows */}
      <div className="bg-[#F9FAFB] border border-[#E5E7EB] rounded-xl overflow-hidden divide-y divide-[#E5E7EB]">

        {/* Project Name */}
        <div className={`flex items-center justify-between px-5 py-4 bg-white ${directChatMode ? 'opacity-50' : ''}`}>
          <div>
            <p className="text-[14px] font-semibold text-[#111827]">{t('projectSetup.fieldProjectName')}</p>
            <p className="text-[12px] text-[#6B7280] mt-0.5">{t('projectSetup.fieldProjectNameDesc')}</p>
          </div>
          <Toggle enabled={nameEnabled} onToggle={() => setNameEnabled((v) => !v)} aispaceColor={aispaceColor} disabled={!isSuperAdmin || directChatMode} />
        </div>

        {/* Project Image */}
        <div className={`flex items-center justify-between px-5 py-4 bg-white ${directChatMode ? 'opacity-50' : ''}`}>
          <div>
            <p className="text-[14px] font-semibold text-[#111827]">{t('projectSetup.fieldProjectImage')}</p>
            <p className="text-[12px] text-[#6B7280] mt-0.5">{t('projectSetup.fieldProjectImageDesc')}</p>
          </div>
          <Toggle enabled={imageEnabled} onToggle={() => setImageEnabled((v) => !v)} aispaceColor={aispaceColor} disabled={!isSuperAdmin || directChatMode} />
        </div>

        {/* Upload Methods */}
        <div className={`px-5 py-4 bg-white ${directChatMode ? 'opacity-50' : ''}`}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[14px] font-semibold text-[#111827]">{t('projectSetup.fieldUploadMethods')}</p>
              <p className="text-[12px] text-[#6B7280] mt-0.5">{t('projectSetup.fieldUploadMethodsDesc')}</p>
            </div>
            <Toggle enabled={uploadEnabled} onToggle={() => setUploadEnabled((v) => !v)} aispaceColor={aispaceColor} disabled={!isSuperAdmin || directChatMode} />
          </div>
          {uploadEnabled && (
            <div className="mt-4">
              <p className="text-[12px] font-medium text-[#374151] mb-2">{t('projectSetup.uploadSourceLabel')}</p>
              <div className="flex gap-2">
                {UPLOAD_SOURCE_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => isSuperAdmin && !directChatMode && setUploadSource(opt.value)}
                    disabled={!isSuperAdmin || directChatMode}
                    className="px-4 py-2 text-[13px] font-medium rounded-lg border transition-colors disabled:cursor-not-allowed disabled:opacity-60"
                    style={{
                      backgroundColor: uploadSource === opt.value ? `${aispaceColor}15` : 'white',
                      borderColor: uploadSource === opt.value ? aispaceColor : '#E5E7EB',
                      color: uploadSource === opt.value ? aispaceColor : '#374151',
                    }}
                  >
                    {t(opt.labelKey)}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

      </div>
    </div>
  )
}

export default ProjectSetupBuilder
